import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import KeyboardDoubleArrowLeftOutlined from '@mui/icons-material/KeyboardDoubleArrowLeftOutlined';
import KeyboardDoubleArrowRightOutlined from '@mui/icons-material/KeyboardDoubleArrowRightOutlined';
import { HEADER_HEIGHT, SIDEBAR_MINI_WIDTH, SIDEBAR_WIDTH } from '@/theme';
import type { RootState } from '@/store';
import ZuulFlames from '../../components/ZuulLogo/ZuulFlames';
import ZuulLogo from '../../components/ZuulLogo/ZuulLogo';
import AppSidebarNav from './AppSidebarNav';

/**
 * Left navigation. Redux remains the state source (same actions as the CoreUI sidebar used):
 * - `sidebarShow`: desktop → drawer visible at all; mobile → temporary drawer open.
 * - `sidebarUnfoldable`: mini variant (icons only) that expands as an overlay on hover.
 */
export default function AppSidebar() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const sidebarShow = useSelector((state: RootState) => Boolean(state.sidebarShow));
  const unfoldable = useSelector((state: RootState) => Boolean(state.sidebarUnfoldable));
  const [hovering, setHovering] = React.useState(false);
  const [brandHover, setBrandHover] = React.useState(false);

  const mini = isDesktop && unfoldable;
  const collapsed = mini && !hovering;
  const dockedWidth = mini ? SIDEBAR_MINI_WIDTH : SIDEBAR_WIDTH;
  const paperWidth = collapsed ? SIDEBAR_MINI_WIDTH : SIDEBAR_WIDTH;
  const closeMobile = () => dispatch({ type: 'set', sidebarShow: false });

  // The glow picks up the mark's own accent (the eyes) so the hover state brands with the logo.
  const logoGlow = `drop-shadow(0 0 8px ${(theme.vars ?? theme).palette.zuul.logoAccent})`;

  return (
    <Drawer
      variant={isDesktop ? 'permanent' : 'temporary'}
      open={sidebarShow}
      onClose={closeMobile}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      sx={{
        width: dockedWidth,
        flexShrink: 0,
        display: isDesktop && !sidebarShow ? 'none' : 'block',
        '& .MuiDrawer-paper': {
          width: paperWidth,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          // Shell surface, matching the header, so the whole frame reads apart from content.
          bgcolor: 'zuul.shell',
          borderRight: 1,
          borderColor: 'divider',
          boxShadow: mini && hovering ? 6 : 'none',
          transition: theme.transitions.create(['width', 'box-shadow'], {
            duration: theme.transitions.duration.shorter,
          }),
        },
      }}
    >
      {/* `dense`, like the header's Toolbar: a regular Toolbar carries MUI's `≥600px → 64px` media rule,
          which outranks the `minHeight` below and left this bar 8px taller than the header beside it. */}
      <Toolbar
        variant='dense'
        disableGutters
        sx={{ minHeight: HEADER_HEIGHT, px: 1.5, justifyContent: collapsed ? 'center' : 'space-between' }}
      >
        {/* The link hugs the artwork but stretches to the toolbar's full height, so the flames have
            the header's clear space to burn in rather than only the logo's own box. */}
        <Box
          component={RouterLink}
          to='/'
          aria-label='Zuul home'
          onMouseEnter={() => setBrandHover(true)}
          onMouseLeave={() => setBrandHover(false)}
          sx={{
            position: 'relative',
            alignSelf: 'stretch',
            display: 'inline-flex',
            alignItems: 'center',
            textDecoration: 'none',
          }}
        >
          <ZuulFlames active={brandHover} />
          <ZuulLogo
            variant={collapsed ? 'mark' : 'horizontal'}
            height={collapsed ? 28 : 32}
            sx={{
              position: 'relative',
              zIndex: 1,
              filter: brandHover ? logoGlow : 'none',
              transform: brandHover ? 'scale(1.1)' : 'none',
              transition: 'transform 0.3s ease, filter 0.3s ease',
            }}
          />
        </Box>
        {!isDesktop && (
          <IconButton size='small' aria-label='Close navigation' onClick={closeMobile}>
            <CloseOutlined />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <AppSidebarNav collapsed={collapsed} onNavigate={isDesktop ? undefined : closeMobile} />
      </Box>
      {isDesktop && (
        <>
          <Divider />
          {/* In mini mode the toggler stays at the same x whether or not hover has expanded the drawer, so it does not slide away from the cursor. */}
          <Box
            sx={{ display: 'flex', justifyContent: mini ? 'flex-start' : 'flex-end', py: 1.6, px: mini ? '15px' : 0.5 }}
          >
            <Tooltip title={unfoldable ? 'Expand sidebar' : 'Collapse sidebar'} placement='right'>
              <IconButton
                size='small'
                aria-label={unfoldable ? 'Expand sidebar' : 'Collapse sidebar'}
                onClick={() => dispatch({ type: 'set', sidebarUnfoldable: !unfoldable })}
              >
                {unfoldable ? <KeyboardDoubleArrowRightOutlined /> : <KeyboardDoubleArrowLeftOutlined />}
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}
    </Drawer>
  );
}
